import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Facebook, Linkedin, Mail, MapPin, Phone, Printer } from "lucide-react";

const quickLinks = [
  { to: "/about", label: "About Us" },
  { to: "/membership", label: "Membership" },
  { to: "/foundation", label: "Foundation" },
  { to: "/resources", label: "Resources" },
  { to: "/events", label: "Events" },
  { to: "/contact", label: "Contact Us" },
];

export const Footer = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;

    setLoading(true);
    const { error } = await (supabase as any)
      .from("newsletter_subscribers")
      .insert({ email: trimmed });

    setLoading(false);

    if (error) {
      toast({
        title: error.code === "23505" ? "Already subscribed" : "Error",
        description:
          error.code === "23505"
            ? "This email is already on our list."
            : "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } else {
      toast({ title: "Subscribed!", description: "You'll hear from us soon." });
      setEmail("");
    }
  };

  return (
    <footer className="border-t bg-card">
      <div className="mx-auto max-w-7xl px-4 py-12">
        <div className="grid gap-8 md:grid-cols-3">
          {/* Contact Info */}
          <div>
            <p className="font-semibold text-foreground mb-3">
              Arizona Council of Human Service Providers
            </p>
            <div className="space-y-2 text-sm text-muted-foreground">
              <p className="flex items-start gap-2">
                <MapPin className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
                2100 N Central Ave #225, Phoenix AZ 85004
              </p>
              <p className="flex items-center gap-2">
                <Phone className="h-4 w-4 flex-shrink-0 text-primary" />
                (602) 252-9363
              </p>
              <p className="flex items-center gap-2">
                <Printer className="h-4 w-4 flex-shrink-0 text-primary" />
                (602) 252-8664
              </p>
            </div>
            <div className="mt-4 flex items-center gap-3">
              <a href="https://www.facebook.com/arizonacouncil/" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="text-muted-foreground hover:text-primary transition-colors">
                <Facebook className="h-5 w-5" />
              </a>
              <a href="https://www.linkedin.com/company/arizona-council-of-human-service-providers/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="text-muted-foreground hover:text-primary transition-colors">
                <Linkedin className="h-5 w-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-semibold mb-3">Quick Links</h3>
            <ul className="space-y-1.5 text-sm">
              {quickLinks.map(({ to, label }) => (
                <li key={to}>
                  <Link to={to} className="text-muted-foreground hover:text-primary transition-colors">
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Mail className="h-5 w-5 text-primary" />
              <h3 className="font-semibold">Stay Updated</h3>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              Subscribe to receive the latest news and resources from ACHS.
            </p>
            <form onSubmit={handleSubscribe} className="flex gap-2">
              <Input
                type="email"
                required
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1"
              />
              <Button type="submit" disabled={loading}>
                {loading ? "…" : "Subscribe"}
              </Button>
            </form>
          </div>
        </div>

        <div className="mt-8 border-t pt-6 text-center text-sm text-muted-foreground">
          &copy; {new Date().getFullYear()} Arizona Council of Human Service Providers. All rights reserved.
        </div>
      </div>
    </footer>
  );
};
